import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Returns a debounced version of the callback.
 * The returned function can be called rapidly — the actual callback
 * only fires after `delay` ms of silence.
 */
export function useDebouncedCallback(callback, delay = 300) {
    const callbackRef = useRef(callback);
    const timerRef = useRef(null);
    const argsRef = useRef([]);

    // Always point to the latest callback without restarting the timer
    callbackRef.current = callback;

    const debounced = useCallback(
        (...args) => {
            argsRef.current = args;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                callbackRef.current(...argsRef.current);
                argsRef.current = [];
            }, delay);
        },
        [delay],
    );

    const flush = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            callbackRef.current(...argsRef.current);
            argsRef.current = [];
            return true;
        }
        return false;
    }, []);

    // Flush pending work when the caller unmounts.
    useEffect(() => () => {
        flush();
    }, [flush]);

    // Expose flush for cases where we need immediate commit (e.g. Done button).
    debounced.flush = flush;

    return debounced;
}

/**
 * Debounces a rapidly-changing value.
 * Returns the debounced value that updates only after `delay` ms of stability.
 */
export function useDebouncedValue(value, delay = 300) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}
